/**
 * Centralized URL resolution utility
 * Handles localhost:3001 URLs and converts them to production backend URLs
 */

import { API_ENDPOINTS, backendConfig, joinBackendPath, toSameOriginMediaUrl } from '../config/backendConfig';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../firebase';

const RESOLVED_URL_CACHE_PREFIX = 'artmosphere:resolved-url:';
const resolvedUrlMemoryCache = new Map();
const inFlightResolutions = new Map();

const readPersistentCache = (key) => {
  if (!key) return null;
  try {
    const fromSession = sessionStorage.getItem(`${RESOLVED_URL_CACHE_PREFIX}${key}`);
    if (fromSession) return fromSession;
  } catch (_) {}
  try {
    const fromLocal = localStorage.getItem(`${RESOLVED_URL_CACHE_PREFIX}${key}`);
    if (fromLocal) return fromLocal;
  } catch (_) {}
  return null;
};

const getCachedResolvedUrl = (key) => {
  if (!key) return null;
  const mem = resolvedUrlMemoryCache.get(key);
  if (mem) return mem;
  const stored = readPersistentCache(key);
  if (stored) {
    resolvedUrlMemoryCache.set(key, stored);
    return stored;
  }
  return null;
};

const setCachedResolvedUrl = (key, value) => {
  if (!key || !value) return;
  resolvedUrlMemoryCache.set(key, value);
  try {
    sessionStorage.setItem(`${RESOLVED_URL_CACHE_PREFIX}${key}`, value);
  } catch (_) {}
  try {
    localStorage.setItem(`${RESOLVED_URL_CACHE_PREFIX}${key}`, value);
  } catch (_) {}
};

const getBackendBase = () => {
  return API_ENDPOINTS?.backendBase ||
    (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
      ? 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
      : 'http://localhost:3001');
};

const normalizeUrl = (value) => {
  if (!value || typeof value !== 'string') return value;
  return value.trim();
};

const looksLikeStoragePath = (value) => {
  if (!value || typeof value !== 'string') return false;
  if (value.startsWith('gs://')) return true;
  if (value.startsWith('/uploads') || value.startsWith('uploads')) return false;
  if (/^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:') || value.startsWith('//')) return false;
  return true;
};

/**
 * Resolves image URLs, converting localhost:3001 URLs to production backend URLs
 * @param {string} imageUrl - The image URL to resolve
 * @returns {string} - The resolved URL
 */
export const resolveImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl;
  const normalized = normalizeUrl(imageUrl);
  if (!normalized) return normalized;

  const cached = getCachedResolvedUrl(normalized);
  if (cached) return cached;

  // Data/blob URLs are already resolvable
  if (normalized.startsWith('data:') || normalized.startsWith('blob:')) {
    setCachedResolvedUrl(normalized, normalized);
    return normalized;
  }

  // Protocol-relative URL
  if (normalized.startsWith('//')) {
    const resolved = `https:${normalized}`;
    setCachedResolvedUrl(normalized, resolved);
    return resolved;
  }

  // Convert Firebase Storage gs://bucket/path to HTTPS media URL
  if (normalized.startsWith('gs://')) {
    const match = normalized.match(/^gs:\/\/([^/]+)\/(.+)$/);
    if (match) {
      const [, bucket, objectPath] = match;
      const resolved = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}?alt=media`;
      setCachedResolvedUrl(normalized, resolved);
      return resolved;
    }
  }

  // Replace localhost:3001 URLs with production backend URL
  if (normalized.includes('localhost:3001')) {
    const backendBase = getBackendBase();
    const urlPath = normalized.replace(/^https?:\/\/[^/]+/, '');
    const resolved = `${backendBase}${urlPath}`;
    setCachedResolvedUrl(normalized, resolved);
    return resolved;
  }

  // Full URLs (already production URLs)
  if (/^https?:\/\//i.test(normalized)) {
    setCachedResolvedUrl(normalized, normalized);
    return normalized;
  }

  // Relative uploads paths
  const backendBase = getBackendBase();
  if (normalized.startsWith('/uploads')) {
    const resolved = `${backendBase}${normalized}`;
    setCachedResolvedUrl(normalized, resolved);
    return resolved;
  }
  if (normalized.startsWith('uploads')) {
    const resolved = `${backendBase}/${normalized}`;
    setCachedResolvedUrl(normalized, resolved);
    return resolved;
  }

  // Same-origin /api/* and /proxy-image from ChooseArtworkModal (dev) must resolve to the real backend
  // on static hosting; joinBackendPath maps `/api/...` paths onto Firebase (`.../cloudfunctions.net/api`)
  // vs Cloud Run (`.../api`) consistently.
  if (normalized.startsWith('/api/') || normalized.startsWith('/proxy-image')) {
    const base = String(backendConfig.baseUrl || '').replace(/\/$/, '');
    if (base) {
      const joined = joinBackendPath(base, normalized);
      const out = toSameOriginMediaUrl(joined);
      setCachedResolvedUrl(normalized, out);
      return out;
    }
  }

  // Generic relative media path from backend storage
  if (!normalized.startsWith('/')) {
    const resolved = `${backendBase}/${normalized}`;
    setCachedResolvedUrl(normalized, resolved);
    return resolved;
  }

  setCachedResolvedUrl(normalized, normalized);
  return normalized;
};

/**
 * Async resolver for storage references. Uses memory+session cache to avoid repeat latency.
 * @param {string} imageUrl
 * @returns {Promise<string>}
 */
export const resolveImageUrlAsync = async (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl;
  const normalized = normalizeUrl(imageUrl);
  if (!normalized) return normalized;

  const cached = getCachedResolvedUrl(normalized);
  if (cached) return cached;
  if (inFlightResolutions.has(normalized)) return inFlightResolutions.get(normalized);

  const work = (async () => {
    if (!looksLikeStoragePath(normalized) && !normalized.startsWith('gs://')) {
      return resolveImageUrl(normalized);
    }
    try {
      const objectPath = normalized.startsWith('/') ? normalized.slice(1) : normalized;
      const downloadUrl = await getDownloadURL(ref(storage, normalized.startsWith('gs://') ? normalized : objectPath));
      setCachedResolvedUrl(normalized, downloadUrl);
      return downloadUrl;
    } catch (_) {
      const fallback = resolveImageUrl(normalized);
      setCachedResolvedUrl(normalized, fallback);
      return fallback;
    }
  })();

  inFlightResolutions.set(normalized, work);
  try {
    return await work;
  } finally {
    inFlightResolutions.delete(normalized);
  }
};

/**
 * Warm URL and decode caches for smoother scrolling.
 * @param {string[]} urls
 * @param {number} limit
 */
export const warmResolvedImageUrls = async (urls = [], limit = 24) => {
  const unique = Array.from(new Set((urls || []).filter(Boolean))).slice(0, limit);
  await Promise.all(
    unique.map(async (raw) => {
      const resolved = await resolveImageUrlAsync(raw);
      if (!resolved || typeof Image === 'undefined') return;
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = resolved;
      } catch (_) {}
    })
  );
};

/**
 * Gets the full backend URL for API calls or image proxying
 * @returns {string} - The backend base URL
 */
export const getBackendBaseUrl = () => {
  return API_ENDPOINTS?.backendBase || 
         (typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
           ? 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
           : 'http://localhost:3001');
};

